import type { InfiniteData } from '@tanstack/react-query'

import type { ClientError } from '@tribeplatform/gql-client/lib'
import type {
  Action,
  MutationReadNotificationArgs,
  PaginatedNotification,
} from '@tribeplatform/gql-client/types'

import {
  useMutation,
  UseMutationOptions,
} from '../../lib/react-query/useMutation.js'
import { useQueryClient } from '../../lib/react-query/useQueryClient.js'
import { useTribeClient } from '../../useTribeClient.js'
import {
  getNotificationsKey,
  getNotificationsCountKey,
} from '../../utils/keys/notification.key.js'

export const useReadNotification = (options?: {
  useMutationOptions?: UseMutationOptions<
    Action,
    ClientError,
    MutationReadNotificationArgs
  >
}) => {
  const { useMutationOptions } = options || {}
  const { client } = useTribeClient()
  const queryClient = useQueryClient()

  return useMutation<Action, ClientError, MutationReadNotificationArgs>(
    input => client.notifications.readNotification(input),
    {
      onMutate: variables => {
        const notificationsKey = getNotificationsKey()
        queryClient.setQueriesData<InfiniteData<PaginatedNotification>>(
          notificationsKey,
          input => {
            const updatedInput: InfiniteData<PaginatedNotification> = {
              ...input,
              pages: input.pages.map(page => ({
                ...page,
                nodes: page.nodes.map(notification => ({
                  ...notification,
                  read:
                    notification.id === variables.notificationId
                      ? true
                      : notification.read,
                })),
                edges: page.edges.map(edge => ({
                  ...edge,
                  node: {
                    ...edge.node,
                    read:
                      edge.node.id === variables.notificationId
                        ? true
                        : edge.node.read,
                  },
                })),
              })),
            }
            return updatedInput
          },
        )
      },
      onSettled: () => {
        const notificationsKey = getNotificationsKey()
        queryClient.invalidateQueries(notificationsKey)

        const notificationsCountKey = getNotificationsCountKey()
        queryClient.invalidateQueries(notificationsCountKey)
      },
      ...useMutationOptions,
    },
  )
}
